import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { db, GoalTemplate, Matter, Project } from '../../lib/db';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: GoalTemplate, matterId?: string, projectId?: string) => void;
  userId: string;
  matters?: Matter[];
  projects?: Project[];
}

export function TemplateModal({ isOpen, onClose, onSelectTemplate, userId, matters, projects }: TemplateModalProps) {
  const [templates, setTemplates] = useState<GoalTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedMatterId, setSelectedMatterId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, userId]);

  useEffect(() => {
    // Validate required props
    if (isOpen && !onSelectTemplate) {
      console.error('TemplateModal: onSelectTemplate prop is required but not provided');
    }
  }, [isOpen, onSelectTemplate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const visibleTemplates = await db.goalTemplates.findVisible(userId);
      setTemplates(visibleTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(templates.map(t => t.category).filter(Boolean))];
  
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      
      <div className="absolute inset-4 md:inset-y-8 md:inset-x-auto md:max-w-3xl md:mx-auto bg-white rounded-lg shadow-xl flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Choose a Deliverable Template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">Loading templates...</div>
          </div>
        ) : (
          <>
            <div className="px-6 py-3 border-b border-gray-200">
              <div className="flex gap-2 overflow-x-auto">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === category
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category === 'all' ? 'All Templates' : category}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4 mb-6">
                {projects && projects.length > 0 && (
                  <div>
                    <label htmlFor="project-select" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Project (Optional)
                    </label>
                    <select
                      id="project-select"
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">No project selected</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {matters && matters.length > 0 && (
                  <div>
                    <label htmlFor="matter-select" className="block text-sm font-medium text-gray-700 mb-2">
                      Select Matter (Optional)
                    </label>
                    <select
                      id="matter-select"
                      value={selectedMatterId}
                      onChange={(e) => setSelectedMatterId(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">No matter selected</option>
                      {matters.map((matter) => (
                        <option key={matter.id} value={matter.id}>
                          {matter.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No templates available in this category.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredTemplates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      if (typeof onSelectTemplate === 'function') {
                        onSelectTemplate(template, selectedMatterId, selectedProjectId);
                      } else {
                        console.error('onSelectTemplate is not a function', onSelectTemplate);
                        toast.error('Unable to select template. Please try again.');
                      }
                    }}
                    className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all text-left group"
                  >
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 mb-1">
                      {template.title}
                    </h3>
                    {template.description && (
                      <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                    )}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {template.category && (
                        <span className="px-2 py-1 bg-gray-100 rounded">
                          {template.category}
                        </span>
                      )}
                      {template.is_system && (
                        <span className="text-blue-600">System Template</span>
                      )}
                      {!template.is_system && (
                        <span className="text-green-600">Custom Template</span>
                      )}
                    </div>
                  </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}